import { useEffect, useMemo, useState } from "react";
import { useKundeObjekt, useUpsertKundeObjekt } from "./useVertraege";
import { useAutoSave } from "./useEntwurf";
import { anschriftTeile } from "../lib/wizard";
import type { GartenPosition, KundeLite } from "../lib/wizard";

/**
 * Das Gartenprofil im Wizard: Flächen und Elemente, Zugang, Anschrift des
 * Pflegeobjekts — mit eigenem Autosave (`kunde_objekt`), unabhängig vom
 * Vertragsentwurf. Aus `WizardPage` herausgelöst (09.10.2026), unverändert.
 */
export function useGartenprofil(kundeId: string, chosen: KundeLite | undefined) {
  const { data: objekt, isSuccess: objektGeladen } = useKundeObjekt(kundeId || undefined);
  const upsertObjekt = useUpsertKundeObjekt();
  const [garten, setGarten] = useState<GartenPosition[]>([]);
  const [zugang, setZugang] = useState("");
  // Anschrift des Pflegeobjekts. Sie hing bisher nirgends: der Vertrag sprach
  // vom „nachstehend bezeichneten Grundstück", bezeichnet wurde es nie.
  const [strasse, setStrasse] = useState("");
  const [plz, setPlz] = useState("");
  const [ort, setOrt] = useState("");
  const [init, setInit] = useState(false);

  // EINMAL laden, nicht bei jedem Refetch — sonst gingen im Vor/Zurück-Flow
  // eingegebene Zeilen verloren (Feedback 24.07. Pkt 3). Ampel ist `isSuccess`,
  // nicht das Ergebnis: ein Kunde ohne Gartenprofil liefert `null`, und daran
  // blieb die Initialisierung vorher für immer hängen.
  useEffect(() => {
    if (init || !objektGeladen) return;
    setGarten(objekt?.positionen?.length ? objekt.positionen : []);
    setZugang(objekt?.zugang_notiz || "");
    setStrasse(objekt?.strasse || "");
    setPlz(objekt?.plz || "");
    setOrt(objekt?.ort || "");
    setInit(true);
  }, [objekt, objektGeladen, init]);

  // Ohne eigene Objektanschrift gilt die des Kunden — in die drei Felder
  // verteilt, nicht als eine Zeile ins Straßenfeld. Eigener Effekt, weil die
  // Kundenliste oft erst nach dem Gartenprofil ankommt; bis zum 28.09.2026
  // blieben die Felder dann für immer leer (Benjamin: „Adresse des Kunden wird
  // nicht ausgefüllt").
  useEffect(() => {
    if (!init || !chosen) return;
    if (strasse || plz || ort) return;
    const a = anschriftTeile(chosen);
    setStrasse(a.strasse);
    setPlz(a.plz);
    setOrt(a.ort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init, chosen]);

  const stand = useMemo(() => ({
    positionen: garten.filter(p => p.typ.trim() !== "").map(p => ({
      typ: p.typ.trim(), groesse: p.groesse.trim(),
      einheit: p.einheit.trim(), zustand: p.zustand.trim(),
    })),
    zugang_notiz: zugang,
    strasse, plz, ort,
  }), [garten, zugang, strasse, plz, ort]);

  const speichern = useAutoSave(
    stand,
    w => upsertObjekt.mutateAsync({ kundeId, body: w }),
    { aktiv: !!kundeId && init },
  );

  return {
    garten, zugang, strasse, plz, ort, speichern,
    setZugang, setStrasse, setPlz, setOrt,
    erfasst: garten.some(p => p.typ.trim() !== "" && p.groesse.trim() !== ""),
    neueZeile: () => setGarten(g => [...g, { typ: "Rasenfläche", groesse: "", einheit: "m²", zustand: "" }]),
    patchZeile: (i: number, wert: Partial<GartenPosition>) =>
      setGarten(g => g.map((x, idx) => (idx === i ? { ...x, ...wert } : x))),
    entferneZeile: (i: number) => setGarten(g => g.filter((_, idx) => idx !== i)),
    /** Beim Kundenwechsel: neu laden, sobald der nächste Kunde feststeht. */
    zuruecksetzen: () => { setGarten([]); setZugang(""); setInit(false); },
  };
}

export type Gartenprofil = ReturnType<typeof useGartenprofil>;
